<template>
  <asc-page-loading-indicator
    :loading="isFleetDataLoading && apiPageFailure.length === 0"
  >
    <div class="asc-fleet-data-container">
      <asc-fleet-data-template
        :current-tab="currentTab"
        :fleet-data="mappedFleetData"
        :invalid-global-filter="invalidGlobalFilterForPhmAgreements"
        :open-maintenance-data="mappedOpenMaintenanceData"
        :open-maintenance-grid-columns="openMaintenanceGridColumns"
        :loading="false"
        :include-internal-scores="includeInternalScores"
        :is-feature-investigator="isFeatureInvestigator"
        :is-issue-investigator="isIssueInvestigator"
        :is-issue-reader="isIssueReader"
        :is-issue-updater="isIssueUpdater"
        :on-open-maintenance="handleOpenMaintenance"
        :on-open-fde="handleOpenFde"
        :on-open-connectivity="handleOpenConnectivity"
        :on-tab-change="handleTabChange"
        :loading-plot="isHealthPlotLoading"
        :fault-msg-default-filter-options="getFaultMsgDefaultFilterOptions"
        :fault-msg-count-by-tail-data="mappedFaultMsgCountByTailData"
        :fault-msg-count-by-ata-chapter-data="
          mappedFaultMsgCountByAtaChapterData
        "
        :fault-msg-count-by-para-data="mappedFaultMsgCountByParaData"
        :fault-msg-codes-grid-columns="faultMsgCodesGridColumns"
        :fault-msg-codes-grid-data="mappedFaultMsgCodesData"
        :fault-reports-default-filter-options="
          getFaultReportsDefaultFilterOptions
        "
        :maintenance-statuses="getStatuses"
        :fault-reports-by-flight-data="mappedFaultReportsByFlightData"
        :on-fault-msg-filters-change="handleFaultMsgFiltersChange"
        :on-fault-msg-tail-change="handleFaultMsgTailChange"
        :on-fault-msg-ata-chapter-change="handleFaultMsgAtaChapterChange"
        :on-fault-msg-para-change="handleFaultMsgParaChange"
        :on-fault-reports-filters-change="handleFaultReportsFiltersChange"
        :on-open-raw-report="handleOpenRawReport"
        :on-view-details="handleViewDetails"
        :on-visualize="handleVisualizeFaultMsgCodes"
        :health-score-default-filter-options="
          getHealthScoreDefaultFilterOptions
        "
        :health-score-by-tail-data="mappedHealthScoreByTailData"
        :health-score-by-feature-data="mappedHealthScoreByFeatureData"
        :health-score-trend-data="mappedHealthScoreTrendData"
        :health-score-phm-alert-data="mappedHealthScorePhmAlertData"
        :health-score-mx-rec-overlays-data="mappedHealthScoreMxRecOverlaysData"
        :on-health-score-filters-change="handleHealthScoreFiltersChange"
        :on-health-score-tail-change="handleHealthScoreTailChange"
        :on-health-score-feature-change="handleHealthScoreFeatureChange"
        :on-toggle-include-internal-scores="handleToggleIncludeInternalScores"
        :fleet-page-open-maintenance-permission="
          fleetPageOpenMaintenancePermission
        "
        :fleet-page-my-fleet-permission="fleetPageMyFleetPermission"
        :fleet-page-fault-message-permission="fleetPageFaultMessagePermission"
        :fleet-page-health-score-permission="fleetPageHealthScorePermission"
      ></asc-fleet-data-template>
    </div>

    <asc-open-maintenance-dialog
      :show-dialog="showOpenMaintenanceDialog"
      :items="openMaintenanceData"
      :tail-number="tailNumber"
      :on-open-full-fleet="handleOpenMaintenanceFullFleet"
      :grid-columns="openMaintenanceGridColumns"
      :on-close="handleCloseDialog"
    ></asc-open-maintenance-dialog>

    <asc-flight-deck-effects-dialog
      :show-dialog="showFlightDeckEffectsDialog"
      :items="flightDeckEffectsData"
      :fde-item="fdeItem"
      :on-open-full-fleet="handleOpenFdeFullFleet"
      :grid-columns="flightDeckEffectsGridColumns"
      :on-close="handleCloseDialog"
    ></asc-flight-deck-effects-dialog>

    <asc-connectivity-dialog
      :show-dialog="showConnectivityDialog"
      :days="connectivityDaysData"
      :tail-number="tailNumber"
      :on-close="handleCloseDialog"
    ></asc-connectivity-dialog>

    <asc-fault-msg-details-dialog
      :title="faultMsgDetailsTitle"
      :show-dialog="faultMsgDetailsDialogVisible"
      :loading="isFaultMsgDetailsLoading"
      :on-close="handleCloseFaultMsgDetailsDialog"
      :fault-messages="mappedFaultMsgDetails"
      :grid-columns="faultMsgDetailsColumns"
      :on-open-raw-report="handleOpenRawReport"
      :on-visualize="handleVisualizeFaultMsgDetails"
    ></asc-fault-msg-details-dialog>

    <asc-raw-report-dialog
      :title="faultMsgRawReportTitle"
      :show-dialog="rawReportDialogVisible"
      :loading="isRawReportTextLoading"
      :on-close="handleCloseDialog"
      :report-text="mappedFaultMsgRawReport"
    ></asc-raw-report-dialog>

    <asc-visualize-fault-code-dialog
      :show-dialog="visualizeDetailsDialogVisible"
      :loading="isVisualizeDetailsLoading"
      :on-close="handleCloseDialog"
      :data="mappedVisualizeDetails"
    ></asc-visualize-fault-code-dialog>
  </asc-page-loading-indicator>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import {
  GLOBAL_FILTER_CONSTS as GF,
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
  FLEET_CONSTS as FDC,
  DPLF_CONSTS as DC,
  ANALYTIC_MODULE_CONSTS as AMC,
  MAINTENANCE_ISSUES_CONSTS as MIC,
} from '@/stores/consts'

import AscFleetDataTemplate from '../../../templates/AscFleetDataTemplate.vue'

import AscPageLoadingIndicator from '../../../molecules/AscPageLoadingIndicator.vue'
import AscConnectivityDialog from '../../../molecules/AscConnectivityDialog.vue'
import AscFlightDeckEffectsDialog from '../../../molecules/AscFlightDeckEffectsDialog.vue'
import AscOpenMaintenanceDialog from '../../../molecules/AscOpenMaintenanceDialog.vue'
import AscRawReportDialog from '../../../molecules/AscRawReportDialog.vue'
import AscFaultMsgDetailsDialog from '../../../molecules/AscFaultMsgDetailsDialog.vue'
import AscVisualizeFaultCodeDialog from '../../../molecules/AscVisualizeFaultCodeDialog.vue'

import AppFeatureFlags from '@/utilities/appFeatureFlags'
import {
  getFaultMsgCodesColumns,
  getOpenMaintenanceGridColumns,
  getFlightDeckEffectsGridColumns,
  getFaultMsgDetailsColumns,
} from '@/utilities/fleet'

import { MX_STATUSES } from '@/utilities/appConsts'

export default {
  name: 'AscFleetDataContainer',

  components: {
    AscPageLoadingIndicator,
    AscConnectivityDialog,
    AscFleetDataTemplate,
    AscFlightDeckEffectsDialog,
    AscOpenMaintenanceDialog,
    AscRawReportDialog,
    AscFaultMsgDetailsDialog,
    AscVisualizeFaultCodeDialog,
  },

  data() {
    return {
      connectivityDaysData: 0,
      currentTab: this.getCurrentTabFromLocalStorage(),
      faultMsgCodesGridColumns: getFaultMsgCodesColumns(),
      faultMsgDetailsColumns: getFaultMsgDetailsColumns(),
      faultMsgDetailsDialogVisible: false,
      faultMsgDetailsTitle: 'Fault Messages',
      faultMsgFilters: {},
      faultMsgRawReportTitle: 'Fault Report(s)',
      fdeItem: null,
      flightDeckEffectsData: [],
      flightDeckEffectsGridColumns: getFlightDeckEffectsGridColumns(),
      healthScoreFilters: {},
      includeInternalScores: this.getIncludeInternalScoresFromLocalStorage(),
      isFaultMsgDetailsLoading: false,
      isFleetDataLoading: false,
      isHealthPlotLoading: false,
      isRawReportTextLoading: false,
      isVisualizeDetailsLoading: false,
      openMaintenanceData: [],
      openMaintenanceGridColumns: getOpenMaintenanceGridColumns(),
      rawReportDialogVisible: false,
      showConnectivityDialog: false,
      showFlightDeckEffectsDialog: false,
      showOpenMaintenanceDialog: false,
      tailNumber: null,
      visualizeDetailsDialogVisible: false,
      isFaultMsgsDrillAvailable: false,
      isHealthScoreDrillAvailable: false,
      isTab0DataLoaded: false,
      isTab1DataLoaded: false,
      isTab2DataLoaded: false,
      isTab3DataLoaded: false,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapState(MIC.NAMESPACE, ['maintenanceStatuses']),

    ...mapGetters(GF.NAMESPACE, ['invalidGlobalFilterForPhmAgreements']),
    ...mapGetters(UC.NAMESPACE, [
      'isFeatureInvestigator',
      'isIssueInvestigator',
      'isIssueReader',
      'isIssueUpdater',
      'fleetPageOpenMaintenancePermission',
      'fleetPageMyFleetPermission',
      'fleetPageFaultMessagePermission',
      'fleetPageHealthScorePermission',
    ]),
    ...mapGetters(LC.NAMESPACE, ['mappedLuAtaChapters']),
    ...mapGetters(FDC.NAMESPACE, [
      'mappedFleetData',
      'mappedOpenMaintenanceData',
      'mappedFlightDeckEffectsData',
      'mappedHealthScoreByTailData',
      'mappedHealthScoreByFeatureData',
      'mappedHealthScoreTrendData',
      'mappedHealthScorePhmAlertData',
      'mappedHealthScoreMxRecOverlaysData',
      'mappedAirlineAgreementTypeIds',
    ]),
    ...mapGetters(DC.NAMESPACE, [
      'mappedFaultMsgCountByTailData',
      'mappedFaultMsgCountByAtaChapterData',
      'mappedFaultMsgCountByParaData',
      'mappedFaultMsgCodesData',
      'mappedFaultReportsByFlightData',
      'mappedDplfMessageCodes',
      'mappedFaultMsgRawReport',
      'mappedFaultMsgDetails',
      'mappedVisualizeDetails',
    ]),
    ...mapGetters(AMC.NAMESPACE, ['mappedAmcAnalyticModules']),

    isFleetDataPageActive() {
      return AppFeatureFlags.getFleetDataPage()
    },

    getFaultMsgDefaultFilterOptions() {
      return [
        {
          optionType: 'timeline',
          optionFilter: {
            timelineIndex: 0,
          },
        },
        {
          optionType: 'autocomplete',
          optionFilters: [
            {
              name: 'ATA Chapter',
              value: '0',
              items: this.mappedLuAtaChapters,
              checkedIds: [],
              propName: 'ataChapterIds',
            },
            {
              name: 'Message Code',
              value: '1',
              items: this.mappedDplfMessageCodes,
              checkedIds: [],
              propName: 'messageCodeIds',
              disableSelectAll: true,
            },
          ],
        },
      ]
    },

    getFaultReportsDefaultFilterOptions() {
      return [
        {
          optionType: 'timeline',
          optionFilter: {
            timelineIndex: 0,
          },
        },
        {
          optionType: 'autocomplete',
          optionFilters: [
            {
              name: 'ATA Chapter',
              value: '0',
              items: this.mappedLuAtaChapters,
              checkedIds: [],
              propName: 'ataChapterIds',
            },
          ],
        },
      ]
    },

    getHealthScoreDefaultFilterOptions() {
      return [
        {
          optionType: 'autocomplete',
          optionFilters: [
            {
              name: 'Analytic',
              value: '0',
              items: this.mappedAmcAnalyticModules,
              checkedIds: [],
              propName: 'analyticModuleIds',
            },
          ],
        },
      ]
    },
    getMaintenenaceIssueStatuses() {
      return this.isIssueUpdater
        ? this.maintenanceStatuses?.filter(
            status => !status?.isTerminal && status?.isExternal,
          )
        : this.maintenanceStatuses?.filter(status => !status?.isTerminal)
    },
    getStatuses() {
      // Filter status based on GF selected airlines
      const filteredStatusByTier = this.getMaintenenaceIssueStatuses.filter(
        status =>
          status.agreementTypeIds
            .map(agreementTypeId =>
              this.mappedAirlineAgreementTypeIds.includes(agreementTypeId),
            )
            .includes(true),
      )

      return filteredStatusByTier.map(status => {
        const foundStatus = MX_STATUSES.find(
          statusConst => statusConst.id === status.id,
        )
        if (foundStatus) {
          return {
            ...status,
            text: status.name,
            styleColor: foundStatus.color,
          }
        }
      })
    },
  },

  watch: {
    '$store.state.globalFilter.globalFilter': {
      deep: true,
      handler() {
        this.isTab0DataLoaded = false
        this.isTab1DataLoaded = false
        this.isTab2DataLoaded = false
        this.isTab3DataLoaded = false
        this.getFleetPageData()
      },
    },

    currentTab(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentTab = newVal
        this.setCurrentTabToLocalStorage(newVal)
        this.loadFleetDataGivenTabIndex()
      }
    },

    includeInternalScores(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.includeInternalScores = newVal
        this.setIncludeInternalScoresToLocalStorage(newVal)
      }
    },
  },

  mounted() {
    if (this.isFleetDataPageActive) {
      this.initFleetPageData()
    }
  },

  methods: {
    ...mapActions(LC.NAMESPACE, [
      LC.GET_ATA_CHAPTERS,
      LC.GET_ATA_SUBCHAPTERS,
      LC.GET_MASTER_MODELS,
    ]),
    ...mapActions(FDC.NAMESPACE, [
      FDC.GET_FLEET_DATA,
      FDC.GET_OPEN_MAINTENANCE_DATA,
      FDC.GET_FLIGHT_DECK_EFFECTS_DATA,
      FDC.GET_HEALTH_SCORE_BY_TAIL_DATA,
      FDC.GET_HEALTH_SCORE_BY_FEATURE_DATA,
      FDC.GET_HEALTH_SCORE_TREND_DATA,
      FDC.GET_HEALTH_SCORE_PHM_ALERT_DATA,
    ]),
    ...mapActions(DC.NAMESPACE, [
      DC.GET_FAULT_MSG_COUNT_BY_TAIL,
      DC.GET_FAULT_MSG_COUNT_BY_ATA_CHAPTER,
      DC.GET_FAULT_MSG_COUNT_BY_PARA,
      DC.GET_FAULT_MSG_CODES,
      DC.GET_MESSAGE_CODES,
      DC.GET_FAULT_REPORTS_BY_FLIGHT,
      DC.GET_FAULT_MSG_RAW_REPORT,
      DC.GET_FAULT_MSG_DETAILS,
      DC.GET_VISUALIZE_DETAILS,
    ]),
    ...mapMutations(DC.NAMESPACE, [
      DC.UPDATE_FAULT_MSG_COUNT_BY_TAIL,
      DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER,
      DC.UPDATE_FAULT_MSG_COUNT_BY_PARA,
      DC.UPDATE_FAULT_MSG_CODES,
      DC.UPDATE_FAULT_REPORTS_BY_FLIGHT,
      DC.UPDATE_FAULT_MSG_DETAILS,
      DC.UPDATE_VISUALIZE_DETAILS,
    ]),
    ...mapMutations(FDC.NAMESPACE, [
      FDC.UPDATE_OPEN_MAINTENANCE_DATA,
      FDC.UPDATE_FLEET_DATA,
      FDC.UPDATE_HEALTH_SCORE_BY_TAIL_DATA,
      FDC.UPDATE_HEALTH_SCORE_BY_FEATURE_DATA,
      FDC.UPDATE_HEALTH_SCORE_TREND_DATA,
      FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA,
    ]),
    ...mapActions(AMC.NAMESPACE, [
      AMC.GET_AMC_ANALYTIC_MODULES,
      AMC.GET_AMC_FEATURES,
    ]),

    ...mapActions(MIC.NAMESPACE, [MIC.GET_MAINTENANCE_STATUSES]),

    async initFleetPageData() {
      // clear data
      this.clearMyFleetStoreData()
      this.clearFaultMsgStoreData()
      this.clearFaultReportsStoreData()
      this.clearHealthScoreStoreData()

      this.updateFaultMsgFilters()
      this.updateFaultReportsFilters()
      this.updateHealthScoreFilters()

      await this.loadFleetDataGivenTabIndex()
    },

    async getFleetPageData() {
      // clear data
      this.clearMyFleetStoreData()
      this.clearFaultMsgStoreData()
      this.clearFaultReportsStoreData()
      this.clearHealthScoreStoreData()

      await this.loadFleetDataGivenTabIndex()
    },

    async loadFleetDataGivenTabIndex() {
      if (this.currentTab === 'myFleet' && this.isTab0DataLoaded) {
        return
      }
      if (this.currentTab === 'openMaintenance' && this.isTab1DataLoaded) {
        return
      }
      if (this.currentTab === 'faultMessages' && this.isTab2DataLoaded) {
        return
      }
      if (this.currentTab === 'healthScore' && this.isTab3DataLoaded) {
        return
      }

      switch (this.currentTab) {
        case 'myFleet':
          await this.loadMyFleet()
          this.isTab0DataLoaded = true
          this.isTab1DataLoaded = true
          break
        case 'openMaintenance':
          await this.loadOpenMaintenance()
          this.isTab1DataLoaded = true
          break
        case 'faultMessages':
          await this.loadFaultMsgs()
          this.isTab2DataLoaded = true
          break
        case 'healthScore':
          await this.loadHealthScore()
          this.isTab3DataLoaded = true
          break
        default:
          break
      }
    },

    async loadMyFleet() {
      // My Fleet and Open Maintenance
      this.isFleetDataLoading = true
      await this[LC.GET_MASTER_MODELS]()
      await this[MIC.GET_MAINTENANCE_STATUSES]()
      if (this.getMaintenenaceIssueStatuses.length) {
        await this[FDC.GET_FLEET_DATA](this.getMaintenenaceIssueStatuses)
      }

      this.isFleetDataLoading = false
    },

    async loadOpenMaintenance() {
      // Open Maintenance
      this.isFleetDataLoading = true
      await this[LC.GET_MASTER_MODELS]()
      await this[MIC.GET_MAINTENANCE_STATUSES]()
      if (this.getMaintenenaceIssueStatuses.length) {
        await this[FDC.GET_OPEN_MAINTENANCE_DATA](
          this.getMaintenenaceIssueStatuses,
        )
      }

      this.isFleetDataLoading = false
    },

    async loadFaultMsgs() {
      // lookups
      this.isFleetDataLoading = true
      await Promise.all([
        this[LC.GET_ATA_CHAPTERS](),
        this[LC.GET_ATA_SUBCHAPTERS](),
        this[DC.GET_MESSAGE_CODES]({
          masterModelIds: [],
        }),
      ])
      this.isFleetDataLoading = false

      // Fault Msgs Analysis
      await this.updateFaultMsgsIncludingDrill(
        this.updateFaultMsgsByAtaChapter,
        this.updateFaultMsgsByPara,
        this.updateFaultMsgsCodes,
      )

      // Fault Reports by Flight
      this.isFleetDataLoading = true
      await this[DC.GET_FAULT_REPORTS_BY_FLIGHT](this.faultReportsFilters)
      this.isFleetDataLoading = false
    },

    async loadHealthScore() {
      // lookups
      await Promise.all([
        this[AMC.GET_AMC_ANALYTIC_MODULES](),
        this[AMC.GET_AMC_FEATURES](),
      ])
      // Health Score
      await this.updateHealthScoreIncludingDrill(
        this.updateHealthScoreByFeature,
        this.updateHealthScoreTrend,
      )
    },

    updateFaultMsgFilters() {
      this.faultMsgFilters = {
        timelineIndex: 0,
        timelineText: '1w',
        radioIndex: '0',
        ataChapterIds: [],
        messageCodeIds: [],
      }
    },

    updateFaultReportsFilters() {
      this.faultReportsFilters = {
        timelineIndex: 0,
        timelineText: '1w',
        radioIndex: '0',
        ataChapterIds: [],
      }
    },

    updateHealthScoreFilters() {
      this.healthScoreFilters = {
        radioIndex: '0',
        analyticModuleIds: [],
      }
    },

    getCurrentTabFromLocalStorage() {
      const tabIndex = localStorage.getItem('fleet_current_tab')
      return tabIndex ? String(tabIndex) : 'openMaintenance'
    },

    setCurrentTabToLocalStorage(tabIndex) {
      localStorage.setItem('fleet_current_tab', tabIndex)
    },

    getIncludeInternalScoresFromLocalStorage() {
      const setting = localStorage.getItem('fleet_include_internal_scores')
      if (setting) {
        return setting === '1'
      }

      return false
    },

    setIncludeInternalScoresToLocalStorage(setting) {
      localStorage.setItem('fleet_include_internal_scores', setting ? '1' : '0')
    },

    clearMyFleetStoreData() {
      this[FDC.UPDATE_OPEN_MAINTENANCE_DATA]([])
      this[FDC.UPDATE_FLEET_DATA]([])
    },

    handleOpenConnectivity(tailNumber, days) {
      this.tailNumber = tailNumber
      this.connectivityDaysData = days
      this.showConnectivityDialog = true
    },

    handleOpenFdeFullFleet() {
      this.currentTab = 'faultMessages'
    },

    handleOpenMaintenanceFullFleet() {
      this.currentTab = 'openMaintenance'
    },

    async handleOpenFde(fdeItem) {
      this.fdeItem = fdeItem
      this.isFleetDataLoading = true
      await this[FDC.GET_FLIGHT_DECK_EFFECTS_DATA]({flightNumber: fdeItem.fdeFlightId, tailNumber: fdeItem.name})
      this.isFleetDataLoading = false
      this.flightDeckEffectsData = this.mappedFlightDeckEffectsData
      this.showFlightDeckEffectsDialog = true
    },

    handleOpenMaintenance(tailNumber) {
      this.tailNumber = tailNumber
      this.openMaintenanceData = this.mappedOpenMaintenanceData.filter(
        item => item.tailNumber === tailNumber,
      )
      this.showOpenMaintenanceDialog = true
    },

    handleCloseDialog() {
      this.showConnectivityDialog = false
      this.showFlightDeckEffectsDialog = false
      this.showOpenMaintenanceDialog = false
      this.rawReportDialogVisible = false
      this.visualizeDetailsDialogVisible = false
    },

    handleCloseFaultMsgDetailsDialog() {
      this.faultMsgDetailsDialogVisible = false
    },

    handleTabChange(whichTab) {
      this.currentTab = whichTab
    },

    clearFaultMsgStoreData() {
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_TAIL]([])
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER]([])
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_PARA]([])
      this[DC.UPDATE_FAULT_MSG_CODES]([])
    },

    async handleFaultMsgFiltersChange(filters) {
      this.faultMsgFilters = filters
      await this.updateFaultMsgsIncludingDrill(
        this.updateFaultMsgsByAtaChapter,
        this.updateFaultMsgsByPara,
        this.updateFaultMsgsCodes,
      )
    },

    async updateFaultMsgsIncludingDrill(ataChapterFunc, paraFunc, codesFunc) {
      this.isFaultMsgsDrillAvailable = false
      this.clearFaultMsgStoreData()

      this.isFleetDataLoading = true
      await this[DC.GET_FAULT_MSG_COUNT_BY_TAIL](this.faultMsgFilters)
      this.isFleetDataLoading = false
      const [filteredTail] = this.mappedFaultMsgCountByTailData
      if (
        this.mappedFaultMsgCountByTailData.length > 0 &&
        filteredTail &&
        ataChapterFunc
      ) {
        await ataChapterFunc(filteredTail, paraFunc, codesFunc)
      }
      this.isFaultMsgsDrillAvailable = true
    },

    async updateFaultMsgsByAtaChapter(filteredTail, paraFunc, codesFunc) {
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_ATA_CHAPTER]([])
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_PARA]([])
      this[DC.UPDATE_FAULT_MSG_CODES]([])

      if (filteredTail) {
        const tailNumber = filteredTail.name

        const options = {
          ...this.faultMsgFilters,
          tailNumber,
        }

        this.isFleetDataLoading = true
        await this[DC.GET_FAULT_MSG_COUNT_BY_ATA_CHAPTER](options)
        this.isFleetDataLoading = false

        const [filteredAtaChapter] = this.mappedFaultMsgCountByAtaChapterData
        if (
          this.mappedFaultMsgCountByAtaChapterData.length > 0 &&
          filteredAtaChapter &&
          paraFunc
        ) {
          const ataChapter = filteredAtaChapter.name.split('-')[0].trim()
          await paraFunc(tailNumber, ataChapter, codesFunc)
        }
      }
    },

    async updateFaultMsgsByPara(tailNumber, ataChapter, codesFunc) {
      this[DC.UPDATE_FAULT_MSG_COUNT_BY_PARA]([])
      this[DC.UPDATE_FAULT_MSG_CODES]([])

      if (tailNumber.length > 0 && ataChapter.length > 0) {
        const options = {
          ...this.faultMsgFilters,
          tailNumber,
          ataChapter,
        }

        this.isFleetDataLoading = true
        await this[DC.GET_FAULT_MSG_COUNT_BY_PARA](options)
        this.isFleetDataLoading = false

        const paras = this.mappedFaultMsgCountByParaData.map(item => item.name)
        if (this.mappedFaultMsgCountByParaData.length > 0 && codesFunc) {
          await codesFunc(tailNumber, ataChapter, paras)
        }
      }
    },

    async updateFaultMsgsCodes(tailNumber, ataChapter, paras) {
      this[DC.UPDATE_FAULT_MSG_CODES]([])
      if (
        this.mappedFaultMsgCountByParaData.length > 0 &&
        tailNumber.length > 0 &&
        ataChapter.length > 0
      ) {
        const options = {
          ...this.faultMsgFilters,
          tailNumber,
          ataChapter,
          paras,
        }

        this.isFleetDataLoading = true
        await this[DC.GET_FAULT_MSG_CODES](options)
        this.isFleetDataLoading = false
      }
    },

    async handleFaultMsgTailChange(tailNumber) {
      if (!this.isFaultMsgsDrillAvailable) {
        return
      }

      if (tailNumber.length > 0) {
        const [filteredTail] = this.mappedFaultMsgCountByTailData.filter(
          item => item.name === tailNumber,
        )
        if (filteredTail) {
          await this.updateFaultMsgsByAtaChapter(filteredTail)
        }
      }
    },

    async handleFaultMsgAtaChapterChange(tailNumber, ataChapter) {
      if (!this.isFaultMsgsDrillAvailable) {
        return
      }

      await this.updateFaultMsgsByPara(tailNumber, ataChapter)
    },

    async handleFaultMsgParaChange(tailNumber, ataChapter, paras) {
      if (!this.isFaultMsgsDrillAvailable) {
        return
      }

      await this.updateFaultMsgsCodes(tailNumber, ataChapter, paras)
    },

    clearFaultReportsStoreData() {
      this[DC.UPDATE_FAULT_REPORTS_BY_FLIGHT]([])
    },

    async handleFaultReportsFiltersChange(filters) {
      this.faultReportsFilters = filters
      this.clearFaultReportsStoreData()
      this.isFleetDataLoading = true
      await this[DC.GET_FAULT_REPORTS_BY_FLIGHT](this.faultReportsFilters)

      this.isFleetDataLoading = false
    },

    clearHealthScoreStoreData() {
      this[FDC.UPDATE_HEALTH_SCORE_BY_TAIL_DATA]([])
      this[FDC.UPDATE_HEALTH_SCORE_BY_FEATURE_DATA]([])
      this[FDC.UPDATE_HEALTH_SCORE_TREND_DATA]({})
      this[FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA]([])
    },

    async handleHealthScoreFiltersChange(filters) {
      this.healthScoreFilters = filters
      this.isFleetDataLoading = true
      await this.updateHealthScoreIncludingDrill(
        this.updateHealthScoreByFeature,
        this.updateHealthScoreTrend,
      )
      this.isFleetDataLoading = false
    },

    handleToggleIncludeInternalScores(option) {
      this.includeInternalScores = option
      this.handleHealthScoreFiltersChange(this.healthScoreFilters)
    },

    async updateHealthScoreIncludingDrill(featureFunc, trendFunc) {
      this.isHealthScoreDrillAvailable = false
      this.clearHealthScoreStoreData()

      this.isFleetDataLoading = true
      const options = {
        ...this.healthScoreFilters,
        includeInternalScores: this.includeInternalScores,
      }
      await this[FDC.GET_HEALTH_SCORE_BY_TAIL_DATA](options)
      this.isFleetDataLoading = false
      const [filteredTail] = this.mappedHealthScoreByTailData
      if (
        this.mappedHealthScoreByTailData.length > 0 &&
        filteredTail &&
        featureFunc
      ) {
        await featureFunc(filteredTail, trendFunc)
      }
      this.isHealthScoreDrillAvailable = true
    },

    async updateHealthScoreByFeature(filteredTail, trendFunc) {
      this[FDC.UPDATE_HEALTH_SCORE_BY_FEATURE_DATA]([])
      this[FDC.UPDATE_HEALTH_SCORE_TREND_DATA]({})
      this[FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA]([])

      if (filteredTail) {
        const tailNumber = filteredTail.name
        const isValid = filteredTail
          ? filteredTail.highScoreSummation > 0 ||
            filteredTail.lowScoreSummation > 0
          : false
        if (isValid) {
          const options = {
            ...this.healthScoreFilters,
            tailNumber,
            includeInternalScores: this.includeInternalScores,
          }
          this.isFleetDataLoading = true
          await this[FDC.GET_HEALTH_SCORE_BY_FEATURE_DATA](options)
          this.isFleetDataLoading = false
          const [filteredFeature] = this.mappedHealthScoreByFeatureData
          if (
            this.mappedHealthScoreByFeatureData.length > 0 &&
            filteredFeature &&
            trendFunc
          ) {
            const feature = filteredFeature.name
            await trendFunc(tailNumber, feature)
          }
        }
      }
    },

    async updateHealthScoreTrend(tailNumber, feature) {
      this[FDC.UPDATE_HEALTH_SCORE_TREND_DATA]({})
      this[FDC.UPDATE_HEALTH_SCORE_PHM_ALERT_DATA]([])

      if (this.mappedHealthScoreByFeatureData.length > 0 && feature) {
        const options = {
          tailNumber,
          feature,
        }

        this.isFleetDataLoading = true
        await this[FDC.GET_HEALTH_SCORE_TREND_DATA](options)
        await this[FDC.GET_HEALTH_SCORE_PHM_ALERT_DATA](options)
        this.isFleetDataLoading = false
      }
    },

    async handleHealthScoreTailChange(tailNumber) {
      if (!this.isHealthScoreDrillAvailable) {
        return
      }

      if (tailNumber.length > 0) {
        const [filteredTail] = this.mappedHealthScoreByTailData.filter(
          item => item.name === tailNumber,
        )
        if (filteredTail) {
          await this.updateHealthScoreByFeature(filteredTail)
        }
      }
    },

    async handleHealthScoreFeatureChange(tailNumber, feature) {
      if (!this.isHealthScoreDrillAvailable) {
        return
      }

      await this.updateHealthScoreTrend(tailNumber, feature)
    },

    async handleOpenRawReport(options) {
      if (options.errorType && options.code) {
        this.faultMsgRawReportTitle = `Fault Report(s) for ${options.errorType} ${options.code} for ${options.tailNumber} on flight ${options.flightNumber}`
      } else {
        this.faultMsgRawReportTitle = `Fault Report(s) for ${options.tailNumber} on flight ${options.flightNumber}`
      }

      const filters = {
        fileLogId: options.fileLogId,
        airlineIcao: options.airlineIcao,
      }

      this.isRawReportTextLoading = true
      this.rawReportDialogVisible = true
      await this[DC.GET_FAULT_MSG_RAW_REPORT](filters)
      this.isRawReportTextLoading = false
    },

    async handleViewDetails(options) {
      this[DC.UPDATE_FAULT_MSG_DETAILS]([])
      this.faultMsgDetailsTitle = `Fault Messages for flight ${options.flightNumber}`
      const filters = {
        ...this.faultReportsFilters,
        ...options,
      }
      this.isFaultMsgDetailsLoading = true
      this.faultMsgDetailsDialogVisible = true
      await this[DC.GET_FAULT_MSG_DETAILS](filters)
      this.isFaultMsgDetailsLoading = false
    },

    async handleVisualizeFaultMsgCodes(params) {
      const options = {
        ...this.faultMsgFilters,
        ...params,
      }
      this[DC.UPDATE_VISUALIZE_DETAILS]({})
      this.isVisualizeDetailsLoading = true
      this.visualizeDetailsDialogVisible = true
      await this[DC.GET_VISUALIZE_DETAILS](options)
      this.isVisualizeDetailsLoading = false
    },

    async handleVisualizeFaultMsgDetails(params) {
      const options = {
        ...this.faultReportsFilters,
        ...params,
      }
      this[DC.UPDATE_VISUALIZE_DETAILS]({})
      this.isVisualizeDetailsLoading = true
      this.visualizeDetailsDialogVisible = true
      await this[DC.GET_VISUALIZE_DETAILS](options)
      this.isVisualizeDetailsLoading = false
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-data-container {
  background-color: $gray-100;

  & .container {
    padding: 16px;
  }
}
</style>
